import React from 'react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

const About = () => {
  const values = [
    {
      icon: '🎯',
      title: 'Innovation',
      description: 'We continuously develop cutting-edge trading technologies to stay ahead of market trends and provide our users with the most advanced trading tools.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: '🔒',
      title: 'Security',
      description: 'Your financial security is our top priority. We implement bank-grade security measures and maintain the highest standards of data protection.',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: '🤝',
      title: 'Transparency',
      description: 'We believe in complete transparency in all our operations, from pricing to policies, ensuring you always know exactly what to expect.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: '⚡',
      title: 'Excellence',
      description: 'We strive for excellence in every aspect of our service, from platform performance to customer support, delivering nothing but the best.',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const milestones = [
    {
      year: '2019',
      title: 'Company Founded',
      description: 'GainwiseFX was established with a vision to democratize cryptocurrency trading for everyone.',
      icon: '🚀'
    },
    {
      year: '2020',
      title: 'Platform Launch',
      description: 'Launched our first trading platform with support for major cryptocurrencies and advanced charting tools.',
      icon: '📈'
    },
    {
      year: '2021',
      title: 'Mobile App Release',
      description: 'Introduced mobile trading applications for iOS and Android, enabling trading on the go.',
      icon: '📱'
    },
    {
      year: '2022',
      title: 'Global Expansion',
      description: 'Expanded operations to serve customers in over 150 countries worldwide.',
      icon: '🌍'
    },
    {
      year: '2023',
      title: 'Advanced Features',
      description: 'Introduced AI-powered trading insights, automated trading bots, and institutional-grade tools.',
      icon: '🤖'
    },
    {
      year: '2024',
      title: 'Market Leadership',
      description: 'Achieved $2.5B+ daily trading volume and became one of the leading cryptocurrency exchanges.',
      icon: '👑'
    }
  ];

  const stats = [
    { number: '500K+', label: 'Active Traders', icon: '👥' },
    { number: '$2.5B+', label: 'Daily Volume', icon: '💰' },
    { number: '150+', label: 'Countries Served', icon: '🌍' },
    { number: '99.9%', label: 'Platform Uptime', icon: '⚡' },
    { number: '50+', label: 'Trading Pairs', icon: '🔄' },
    { number: '24/7', label: 'Customer Support', icon: '🎧' }
  ];

  const services = [
    {
      title: 'Spot Trading',
      description: 'Trade cryptocurrencies at current market prices with instant execution and competitive fees.',
      features: ['Real-time pricing', 'Low trading fees', 'High liquidity', 'Multiple order types']
    },
    {
      title: 'Margin Trading',
      description: 'Amplify your trading potential with leverage up to 100x on selected cryptocurrency pairs.',
      features: ['Up to 100x leverage', 'Risk management tools', 'Stop-loss orders', 'Position monitoring']
    },
    {
      title: 'Futures Trading',
      description: 'Trade cryptocurrency futures contracts with advanced risk management and hedging tools.',
      features: ['Perpetual contracts', 'Quarterly futures', 'Cross & isolated margin', 'Advanced charting']
    },
    {
      title: 'Staking Services',
      description: 'Earn passive income by staking your cryptocurrencies with competitive annual yields.',
      features: ['Multiple staking options', 'Flexible terms', 'Competitive yields', 'Auto-compound']
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              About 
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                {' '}GainwiseFX
              </span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Empowering traders worldwide with innovative technology, unmatched security, 
              and exceptional service in the cryptocurrency trading ecosystem.
            </p>
          </div>
        </section>

        {/* Mission & Vision Section */}
        <section className="py-20 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Mission */}
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-2xl mr-4">
                    🎯
                  </div>
                  <h2 className="text-3xl font-bold text-white">Our Mission</h2>
                </div>
                <p className="text-slate-300 text-lg leading-relaxed">
                  To democratize access to cryptocurrency trading by providing a secure, intuitive, 
                  and powerful platform that empowers both novice and professional traders to achieve 
                  their financial goals. We strive to break down barriers and make sophisticated 
                  trading tools accessible to everyone, regardless of their experience level.
                </p>
              </div>

              {/* Vision */}
              <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-2xl mr-4">
                    🌟
                  </div>
                  <h2 className="text-3xl font-bold text-white">Our Vision</h2>
                </div>
                <p className="text-slate-300 text-lg leading-relaxed">
                  To become the world's most trusted and innovative cryptocurrency exchange, 
                  setting new standards for security, user experience, and trading technology. 
                  We envision a future where cryptocurrency trading is as accessible and reliable 
                  as traditional financial services, powered by cutting-edge technology and 
                  unwavering commitment to our users.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Trusted by Millions</h2>
              <p className="text-xl text-slate-300">Our numbers speak for themselves</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className="text-center group hover:scale-105 transition-transform duration-300"
                >
                  <div className="text-4xl mb-3 group-hover:scale-110 transition-transform duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">
                    {stat.number}
                  </div>
                  <div className="text-slate-400 group-hover:text-slate-300 transition-colors text-sm">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Core Values</h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                The principles that guide everything we do and drive our commitment to excellence
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div
                  key={value.title}
                  className="group hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="h-full bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 rounded-2xl p-6 relative overflow-hidden">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${value.gradient} mb-4 text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      {value.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-blue-300 transition-colors">
                      {value.title}
                    </h3>
                    <p className="text-slate-300 leading-relaxed">{value.description}</p>
                    
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-purple-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Services</h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Comprehensive trading solutions designed to meet every trader's needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <div
                  key={service.title}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 group"
                >
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 group-hover:bg-blue-300"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-20 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Journey</h2>
              <p className="text-xl text-slate-300">Key milestones in our growth story</p>
            </div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-gradient-to-b from-blue-500 to-purple-500"></div>
              
              <div className="space-y-12">
                {milestones.map((milestone, index) => (
                  <div
                    key={milestone.year}
                    className={`relative flex items-center ${
                      index % 2 === 0 ? 'justify-start' : 'justify-end'
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full border-4 border-slate-900 z-10"></div>
                    
                    {/* Content */}
                    <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-blue-500/50 transition-all duration-300 group">
                        <div className="flex items-center mb-3">
                          <span className="text-2xl mr-3">{milestone.icon}</span>
                          <span className="text-blue-400 font-bold text-lg">{milestone.year}</span>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-300 transition-colors">
                          {milestone.title}
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                          {milestone.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-12 backdrop-blur-sm border border-slate-700/50">
              <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Trading Journey?</h2>
              <p className="text-xl text-slate-300 mb-8">
                Join our community of successful traders and experience the future of cryptocurrency trading
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 text-white px-8 py-3 rounded-lg font-medium shadow-lg"
                  onClick={() => window.location.href = '/signup'}
                >
                  Get Started Today
                </button>
                <button 
                  className="w-full sm:w-auto text-white border-2 border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300 px-8 py-3 rounded-lg font-medium"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;